
import { Component, Vue, Watch } from 'vue-property-decorator'

import ChatMessageComponent from './ChatMessageComponent.vue'
import ChatInputComponent from './ChatInputComponent.vue'
import ChatAttachmentUploadComponent from './ChatAttachmentUploadComponent.vue'
import ChatMessageDeleteModal from './ChatMessageDeleteModal.vue'
import ChatMessageChangeChallengeModal from './ChatMessageChangeChallengeModal.vue'
import DriveUploader from './DriveUploader.vue'

import { ChatMessage } from '../../lib/chat/ChatMessage'
import { ChatService } from '@/lib/chat/ChatService'
import { ChatAttachment } from '@/lib/chat/ChatAttachment'
import { User } from '../../lib/models/User'
import { RallyRound } from '../../lib/models/RallyRound'
import { DateUtils } from '@/lib/utils/DateUtils'
import { Game } from '../../lib/models/Game'
import UIkit from 'uikit'
import { nextTick } from 'vue'
import { VProgressCircular } from 'vuetify/lib'

@Component({
  components: {
    ChatMessageComponent,
    ChatInputComponent,
    ChatAttachmentUploadComponent,
    ChatMessageDeleteModal,
    ChatMessageChangeChallengeModal,
    DriveUploader,
    VProgressCircular
  }
})
export default class ChatComponent extends Vue {
  chatService: ChatService | null = null
  oldMessagesLoading = false
  scrollDistanceToBottomBeforeNewMessages = 0
  oldNumberOfChatMessages = 0

  messagesHeight = '50vh'

  selectedChallenge: Game | null = null
  important = false
  own = false

  selectedDeleteChatMessageId = -1
  selectedChangeChatMessageId = -1

  locked = false

  showNoConnectionWarning = false

  @Watch('important')
  onImportantChange () {
    setTimeout(() => this.scrollToBottom(false), 0)
  }

  @Watch('$store.state.Chat.driveUploaderSelected')
  onDriveUploaderSelectedChange () {
    setTimeout(() => this.scrollToBottom(false), 0)
  }

  @Watch('$store.state.Chat.pickedChallenge')
  onPickedChallengeChange () {
    setTimeout(() => this.scrollToBottom(false), 0)
  }

  get driveUploaderSelected () {
    return this.$store.state.Chat.driveUploaderSelected
  }

  /* get isLoadingMessages () {
    return this.$store.state.Chat.loadingMessages
  } */

  isLoadingMessages = false

  get sendQueue (): ChatMessage[] {
    if (!this.chatService) {
      return []
    }
    return this.chatService.sendQueue.sendQueue
  }

  isFirstMessage (uuid: string) {
    if (!this.sortedFilteredChatMessages || !this.sortedFilteredChatMessages.length) {
      return false
    }
    if (this.sortedFilteredChatMessages[0].uuid === uuid) {
      return true
    }
    return false
  }

  checkIfNameShouldBeDisplayed (messages: ChatMessage[], i: number, sendQueue = false) {
    if (sendQueue) {
      return false
    }

    // first element in row (last cause reversed)
    if (i === messages.length - 1) {
      return true
    }

    if (!messages[i + 1] || !messages[i]) {
      return true
    }

    const beforeMessageUser = messages[i + 1].user
    const currentMessageUser = messages[i].user

    if (!beforeMessageUser || !currentMessageUser) {
      return true
    }
    if (!beforeMessageUser.id || !currentMessageUser.id) {
      return true
    }
    if (beforeMessageUser.id !== currentMessageUser.id) {
      return true
    }
    return false
  }

  formatMessagesDate (dateString: string) {
    const date = new Date(dateString)

    if (DateUtils.isToday(date)) {
      return 'Heute'
    }
    if (DateUtils.isYesterday(date)) {
      return 'Gestern'
    }

    return `${date.getDate()}. ${date.toLocaleString('default', { month: 'long' })}`
  }

  get chatMessageByDate () {
    const chatMessagesDate: any = {}

    this.sortedFilteredChatMessages.forEach((chatMessage: ChatMessage) => {
      const dateString = DateUtils.dateToMuricaString(new Date(chatMessage.createdAt))
      if (!chatMessagesDate[dateString]) {
        chatMessagesDate[dateString] = []
      }
      chatMessagesDate[dateString].unshift(chatMessage)
    })

    return chatMessagesDate
  }

  get sortedChatMessages () {
    // all this if's are caching magic
    const rallyRoundId = this.$store.state.PageURLRallyRound.rallyRound.id
    if (!this.$store.state.Chat.chatMessages.length && rallyRoundId) {
      const cachedMessagesJson = localStorage.getItem(`chat-messages-${rallyRoundId}`)
      if (cachedMessagesJson) {
        return JSON.parse(cachedMessagesJson).sort((a: ChatMessage, b: ChatMessage) => {
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        })
      }
    } else if (!this.$store.state.Chat.chatMessages.length && rallyRoundId) {
      if (rallyRoundId !== this.$store.state.Chat.chatMessages[0].rallyRoundId) {
        const cachedMessagesJson = localStorage.getItem(`chat-messages-${rallyRoundId}`)
        if (cachedMessagesJson) {
          return JSON.parse(cachedMessagesJson).sort((a: ChatMessage, b: ChatMessage) => {
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          })
        } else {
          return []
        }
      }
    }

    return this.$store.state.Chat.chatMessages.sort((a: ChatMessage, b: ChatMessage) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    })
  }

  renderAmount = 20 // render ammount in messages (not date)
  get sortedFilteredChatMessages () {
    const sortedChatMessagesRenderLimited = this.sortedChatMessages.slice(-this.renderAmount)

    if (this.important) {
      return sortedChatMessagesRenderLimited
        .filter((chatMessage: ChatMessage) => chatMessage.important)
    }

    if (this.own && this.$store.state.LoggedInUser.user.id) {
      return sortedChatMessagesRenderLimited
        .filter((chatMessage: ChatMessage) => chatMessage.user?.id === this.$store.state.LoggedInUser.user.id)
    }

    if (!this.selectedChallenge) {
      return sortedChatMessagesRenderLimited
    }
    return sortedChatMessagesRenderLimited
      .filter((message: ChatMessage) => {
        if (!this.selectedChallenge) {
          return true
        }
        return message.gameId === this.selectedChallenge.id
      })
  }

  @Watch('$store.state.Chat.chatMessages')
  onDateMessagesChange () {
    const messages = this.$refs.messages as HTMLElement
    // is user has not scrolled up very far, scroll to bottom if a new message arrives (if he's at the top loading older messages he shouldn't be pushed to the bottom)
    if ((messages.scrollHeight - messages.clientHeight - messages.scrollTop) < 200) {
      console.log('scrolling to bottom cause pos < 200')
      this.scrollToBottom()
      this.oldNumberOfChatMessages = this.$store.state.Chat.chatMessages.length
    } else {
      if (this.$store.state.Chat.chatMessages.length !== this.oldNumberOfChatMessages) {
        if (this.beforeLoadMessage) {
          setTimeout(() => {
            const oldMessageContainer = (this.$refs.loadedMessages as any[]).find(loadedMessage => {
              return this.beforeLoadMessage === loadedMessage.chatMessage.uuid
            })
            if (oldMessageContainer) {
              if (oldMessageContainer.$el.offsetTop) {
                messages.scrollTo({ left: 0, top: oldMessageContainer.$el.offsetTop - 100, behavior: 'smooth' })
              }
            }
          })
        }
      }
      this.oldNumberOfChatMessages = this.$store.state.Chat.chatMessages.length
    }
    setTimeout(() => {
      this.oldMessagesLoading = false
    }, 1000)
  }

  scrollToBottom (smooth = true) {
    setTimeout(() => {
      const messages = this.$refs.messages as HTMLElement

      messages.scrollTo({ left: 0, top: messages.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
    })
  }

  scrollHeightBefore = 0
  scrollTopBefore = 0
  @Watch('renderAmount')
  onRenderAmountChange () {
    const messages = this.$refs.messages as HTMLElement

    this.$nextTick(() => {
      messages.scrollTo({ left: 0, top: (messages.scrollHeight - this.scrollHeightBefore) + this.scrollTopBefore })
    })
  }

  beforeLoadMessage = null
  onMessageScroll () {
    const messages = this.$refs.messages as HTMLElement

    if (messages.scrollTop < 300) {
      this.scrollHeightBefore = messages.scrollHeight
      this.scrollTopBefore = messages.scrollTop

      this.renderAmount += 20

      const lastMessage = this.sortedChatMessages[0]
      if (!lastMessage) {
        return // seems like messages aren't loaded yet or don't exist
      }
      if (!this.chatService) {
        // chat service doesn't yet exist
      }
    }
  }

  calculateMessageHeight () {
    this.messagesHeight = (window.innerHeight -
      document.getElementsByTagName('header')[0]
        .children[0].getBoundingClientRect().height -
      document.getElementsByClassName('new-nav-container')[0]
        .getBoundingClientRect().height -
        15 - // margin new-nav-container
        30 - // challenges header
        40 - // footer
        17) + // unknown offset
       'px'
  }

  mounted () {
    this.$store.dispatch('loadLoggedInUser')
    this.$store.dispatch('loadRallyRound')
    this.scrollToBottom(false)
    setTimeout(() => (this.$refs.messages as HTMLElement).addEventListener('scroll', this.onMessageScroll))

    // chat messages height
    window.addEventListener('resize', this.calculateMessageHeight)
    setTimeout(this.calculateMessageHeight, 100)
    this.calculateMessageHeight()

    setTimeout(() => this.scrollToBottom(false), 500)

    this.$store.dispatch('loadAllMessages', this.$route.params.rallyRoundId)

    if (this.$store.state.PageURLRallyRound.rallyRound.id) {
      this.chatService = new ChatService(ChatService.CHAT_TYPE_RALLY_ROUND, this.$store.state.PageURLRallyRound.rallyRound.id)
      this.chatService.initiate()
    }

    setTimeout(() => { this.isLoadingMessages = false }, 10000)

    setTimeout(() => { this.showNoConnectionWarning = true }, 10000)
  }

  changeChallenge (id: number) {
    this.selectedChangeChatMessageId = id
    UIkit.modal('#chat-message-change-challenge').show()
  }

  deleteMessage (id: number) {
    this.selectedDeleteChatMessageId = id
    UIkit.modal('#chat-message-delete').show()
  }

  @Watch('$store.state.PageURLRallyRound.rallyRound')
  onRallyRoundChange (rallyRound: RallyRound) {
    if (rallyRound.id) {
      this.chatService = new ChatService(ChatService.CHAT_TYPE_RALLY_ROUND, rallyRound.id)
      this.chatService.initiate()
    }
  }

  tryCreateChatService () {
    if (this.$store.state.PageURLRallyRound.rallyRound.id) {
      this.chatService = new ChatService(ChatService.CHAT_TYPE_RALLY_ROUND, this.$store.state.PageURLRallyRound.rallyRound.id)
      this.chatService.initiate()
    }
  }

  changeSelectedChallenge (challenge: Game) {
    this.selectedChallenge = challenge
  }

  changeImportant (important: boolean) {
    this.own = false
    this.important = important
  }

  changeOwn (own: boolean) {
    this.important = false
    this.own = own
  }
}
